import React, { useState, useEffect } from 'react';
import { 
  Mountain, Download, RefreshCw, Loader2, AlertTriangle, 
  CheckCircle2, Search, Filter, X, Star
} from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { QrDownloadButton } from '@/components/admin/AdminShare';

const AdminTrails = () => {
  const [stats, setStats] = useState(null);
  const [trails, setTrails] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importingAlerts, setImportingAlerts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterView, setFilterView] = useState('all'); // 'all', 'featured', 'not_featured'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, trailsRes, alertsRes] = await Promise.all([
        api.getTrailStats(),
        api.getTrails({ limit: 500 }),
        api.getActiveTrailAlerts()
      ]);
      setStats(statsRes.data);
      setTrails(trailsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Error loading trails data:', error);
      setStats({ total_imported: 0, senderos: 0, bicicletas_caballos: 0, vehiculos_motor: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api.importTrails();
      toast.success(`Importados ${res.data.imported} senderos nuevos, ${res.data.updated} actualizados`);
      loadData();
    } catch (error) {
      console.error('Error importing trails:', error);
      toast.error('Error al importar senderos');
    } finally {
      setImporting(false);
    }
  };

  const handleImportAlerts = async () => {
    setImportingAlerts(true);
    try {
      const res = await api.importTrailAlerts();
      if (res.data.success) {
        toast.success(`${res.data.alerts_new} alertas nuevas encontradas`);
      } else {
        toast.info('No se pudo acceder a tenerifeon.es (protegido por Cloudflare). Puedes crear alertas manualmente.');
      }
      loadData();
    } catch (error) {
      toast.error('No se pudo importar alertas automaticamente');
    } finally {
      setImportingAlerts(false);
    }
  };

  const handleDeactivateAlert = async (alertId) => {
    try {
      await api.deactivateTrailAlert(alertId);
      toast.success('Alerta desactivada');
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (error) {
      toast.error('Error al desactivar la alerta');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadData();
      return;
    }
    try {
      const res = await api.getTrails({ search: searchQuery, limit: 500 });
      setTrails(res.data);
    } catch (error) {
      console.error('Error searching trails:', error);
    }
  };

  const filteredTrails = trails.filter(t => {
    if (filterView === 'featured') return t.featured;
    if (filterView === 'not_featured') return !t.featured;
    return true;
  });

  const featuredCount = trails.filter(t => t.featured).length;

  const handleToggleFeatured = async (trail) => {
    const newFeatured = !trail.featured;
    try {
      await api.toggleTrailFeatured(trail.id, newFeatured);
      setTrails(prev => prev.map(t => t.id === trail.id ? { ...t, featured: newFeatured } : t));
      toast.success(newFeatured ? `"${trail.name}" marcado como destacado` : `"${trail.name}" quitado de destacados`);
    } catch (error) {
      toast.error('Error al cambiar estado destacado');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Fácil': return 'bg-green-100 text-green-700';
      case 'Moderada': return 'bg-yellow-100 text-yellow-700';
      case 'Difícil': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#38B2AC]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-trails">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
            <Mountain className="text-[#38B2AC]" size={24} />
          </div>
          <div>
            <h2 className="font-bold text-lg text-gray-900">Senderos de Tenerife</h2>
            <p className="text-sm text-gray-500">Datos oficiales del Cabildo de Tenerife</p>
          </div>
        </div>
        <Button 
          onClick={handleImport}
          disabled={importing}
          className="bg-[#38B2AC] hover:bg-[#319795] text-white gap-2"
          data-testid="import-trails-btn"
        >
          {importing ? (
            <>
              <Loader2 className="animate-spin" size={16} />
              Importando...
            </>
          ) : (
            <>
              <Download size={16} />
              Importar desde CKAN
            </>
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-2xl font-bold text-gray-900">{stats?.total_imported || 0}</p>
          <p className="text-sm text-gray-500">Total senderos</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-2xl font-bold text-[#38B2AC]">{stats?.senderos || 0}</p>
          <p className="text-sm text-gray-500">Senderos a pie</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-2xl font-bold text-blue-500">{stats?.bicicletas_caballos || 0}</p>
          <p className="text-sm text-gray-500">Bici/Caballo</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-2xl font-bold text-orange-500">{stats?.vehiculos_motor || 0}</p>
          <p className="text-sm text-gray-500">Vehículos</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-amber-200 bg-amber-50">
          <p className="text-2xl font-bold text-amber-500">{featuredCount}</p>
          <p className="text-sm text-gray-500">Destacados</p>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-amber-600" size={18} />
              <span className="font-medium text-amber-800">{alerts.length} alertas activas</span>
            </div>
            <button
              onClick={handleImportAlerts}
              disabled={importingAlerts}
              className="text-xs text-amber-700 underline hover:text-amber-900"
            >
              {importingAlerts ? 'Buscando...' : 'Actualizar alertas'}
            </button>
          </div>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div key={alert.id} className="flex items-center justify-between bg-white rounded-lg p-2 border border-amber-100">
                <div className="flex-1 min-w-0">
                  <span className={`text-xs px-1.5 py-0.5 rounded mr-2 ${
                    alert.alert_type === 'meteorologica' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {alert.alert_type === 'meteorologica' ? 'Meteo' : 'Sendero'}
                  </span>
                  <span className="text-sm text-gray-700">{alert.title}</span>
                </div>
                <button
                  onClick={() => handleDeactivateAlert(alert.id)}
                  className="text-red-400 hover:text-red-600 p-1 flex-shrink-0 ml-2"
                  title="Desactivar alerta"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Last Update Info */}
      {stats?.last_import && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CheckCircle2 size={16} className="text-green-500" />
          Última actualización: {new Date(stats.last_import).toLocaleString('es-ES')}
        </div>
      )}

      {/* Filter tabs: All / Featured / Not featured */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilterView('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            filterView === 'all' ? 'bg-[#38B2AC] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="filter-all-trails"
        >
          Todos ({trails.length})
        </button>
        <button
          onClick={() => setFilterView('featured')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
            filterView === 'featured' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="filter-featured-trails"
        >
          <Star size={14} fill={filterView === 'featured' ? 'currentColor' : 'none'} />
          Destacados ({featuredCount})
        </button>
        <button
          onClick={() => setFilterView('not_featured')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            filterView === 'not_featured' ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="filter-not-featured-trails"
        >
          Sin destacar ({trails.length - featuredCount})
        </button>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            type="text"
            placeholder="Buscar sendero por nombre o código..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={handleSearch}>
          Buscar
        </Button>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw size={16} />
        </Button>
      </div>

      {/* Trails List */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Código</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Nombre</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Tipo</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Dificultad</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Distancia</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Municipio</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Destacada</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrails.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    {trails.length === 0 
                      ? 'No hay senderos importados. Haz clic en "Importar desde CKAN" para comenzar.'
                      : 'No se encontraron senderos con este filtro.'
                    }
                  </td>
                </tr>
              ) : (
                filteredTrails.map((trail) => (
                  <tr key={trail.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                        {trail.matricula || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {trail.has_alert && <AlertTriangle size={14} className="text-amber-500" />}
                        <a 
                          href={trail.tenerifeon_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-[#38B2AC] truncate max-w-[200px]"
                        >
                          {trail.name}
                        </a>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{trail.trail_class}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={getDifficultyColor(trail.difficulty)}>
                        {trail.difficulty}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {(trail.distance_meters / 1000).toFixed(1)} km
                    </td>
                    <td className="px-4 py-3 text-gray-600 truncate max-w-[150px]">
                      {trail.municipalities?.slice(0, 2).join(', ')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleFeatured(trail)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          trail.featured 
                            ? 'text-amber-500 bg-amber-50 hover:bg-amber-100' 
                            : 'text-gray-300 hover:text-amber-400 hover:bg-gray-100'
                        }`}
                        data-testid={`toggle-featured-${trail.id}`}
                        title={trail.featured ? 'Quitar de destacados' : 'Marcar como destacado'}
                      >
                        <Star size={18} fill={trail.featured ? 'currentColor' : 'none'} />
                      </button>
                      <QrDownloadButton kind="t" itemId={trail.id} name={trail.name} className="p-1.5" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trails count */}
      <div className="text-sm text-gray-500">
        Mostrando {filteredTrails.length} de {trails.length} senderos
      </div>

      {/* Source Info */}
      <div className="text-center text-xs text-gray-400">
        Fuente: <a href="https://datos.tenerife.es" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
          Portal de Datos Abiertos del Cabildo de Tenerife
        </a>
      </div>
    </div>
  );
};

export default AdminTrails;
